// PATH: src/components/GlobalSearch.tsx — recherche globale (Ctrl/⌘ + K).
//
// Rejoindre un dossier depuis n'importe quel écran, sans repasser par la liste
// des élèves et ses filtres. Trois familles : élèves (recherche serveur, la même
// que la liste), personnel et classes (filtrés côté client, les référentiels
// tenant en mémoire).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Input, Modal, Spin, Tag, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSchoolApi } from "../api/school";
import { usePedagogyApi } from "../api/pedagogy";
import { useSchoolContext } from "../hooks/useSchoolContext";
import { useRecentStudents } from "../hooks/useRecentStudents";
import type { SchoolClass, Student } from "../models/school";
import type { Staff } from "../api/pedagogy";

type Hit =
  | { kind: "student"; id: number; label: string; hint?: string }
  | { kind: "staff"; id: number; label: string; hint?: string }
  | { kind: "class"; id: number; label: string; hint?: string };

const KIND_LABEL: Record<Hit["kind"], string> = {
  student: "Élève",
  staff: "Personnel",
  class: "Classe",
};

// normalize : comparaison insensible à la casse ET aux accents — « BENALI » doit
// répondre à « benali » comme à « bénali ».
const normalize = (v: string) =>
  v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const GlobalSearch: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const school = useSchoolApi();
  const pedagogy = usePedagogyApi();
  const navigate = useNavigate();
  const { yearId, setClassId } = useSchoolContext();
  const { recent, refresh } = useRecentStudents();

  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [active, setActive] = useState(0);

  // Référentiels chargés à l'ouverture seulement : la palette sert plusieurs
  // fois par session, les recharger à chaque frappe serait inutile.
  useEffect(() => {
    if (!open) return;
    refresh();
    pedagogy.listStaff().then(setStaff).catch(() => setStaff([]));
    if (yearId) school.listClasses(yearId).then(setClasses).catch(() => setClasses([]));
    else setClasses([]);
  }, [open, pedagogy, school, yearId, refresh]);

  // Remise à zéro à la fermeture : rouvrir la palette repart d'un champ vide.
  useEffect(() => {
    if (!open) {
      setText("");
      setQ("");
      setStudents([]);
      setActive(0);
    }
  }, [open]);

  // Recherche différée, même seuil que la liste des élèves : une requête par
  // frappe saturerait l'API.
  const debounce = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQ(text), 250);
    return () => clearTimeout(debounce.current);
  }, [text]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setStudents([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    school
      .listStudents({ q: term, page_size: 8 })
      .then((r) => { if (!cancelled) setStudents(r.items ?? []); })
      .catch(() => { if (!cancelled) setStudents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [school, q]);

  const hits = useMemo<Hit[]>(() => {
    const term = normalize(q);
    if (term.length < 2) return [];
    const out: Hit[] = students.map((s) => ({
      kind: "student",
      id: s.id,
      label: `${s.last_name} ${s.first_name}`,
      hint: s.massar_code || undefined,
    }));
    staff
      .filter((s) => normalize(`${s.last_name} ${s.first_name}`).includes(term))
      .slice(0, 5)
      .forEach((s) => out.push({ kind: "staff", id: s.id, label: `${s.last_name} ${s.first_name}`, hint: s.role }));
    classes
      .filter((c) => normalize(c.label).includes(term))
      .slice(0, 5)
      .forEach((c) => out.push({ kind: "class", id: c.id, label: c.label }));
    return out;
  }, [q, students, staff, classes]);

  // Sans recherche en cours, la palette propose les derniers dossiers ouverts.
  const showingRecent = normalize(q).length < 2 && recent.length > 0;
  const rows: Hit[] = showingRecent
    ? recent.map((r) => ({ kind: "student", id: r.id, label: r.name }))
    : hits;

  useEffect(() => setActive(0), [q, showingRecent]);

  const go = useCallback(
    (hit: Hit) => {
      onClose();
      if (hit.kind === "student") navigate(`/students/${hit.id}`);
      else if (hit.kind === "staff") navigate("/personnel");
      else {
        // Choisir une classe la met en contexte de travail : les écrans qui la
        // consomment (pédagogie, élèves, paiements) s'ouvrent alors dessus.
        setClassId(hit.id);
        navigate("/pedagogie");
      }
    },
    [navigate, onClose, setClassId]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (rows.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % rows.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + rows.length) % rows.length); }
    if (e.key === "Enter") { e.preventDefault(); go(rows[active]); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnClose
      width={620}
      styles={{ body: { padding: 0 } }}
      // Ancrée haut : une palette de commandes ne se cherche pas au centre.
      style={{ top: 80 }}
    >
      <div style={{ padding: 12, borderBottom: "1px solid var(--app-border)" }}>
        <Input
          autoFocus
          size="large"
          variant="borderless"
          prefix={<SearchOutlined style={{ color: "var(--app-muted)" }} />}
          placeholder="Rechercher un élève, un membre du personnel, une classe…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          suffix={loading ? <Spin size="small" /> : null}
        />
      </div>

      <div style={{ maxHeight: 380, overflowY: "auto" }}>
        {showingRecent && (
          <Typography.Text type="secondary" style={{ display: "block", padding: "10px 16px 4px", fontSize: 12 }}>
            Derniers dossiers consultés
          </Typography.Text>
        )}
        {rows.map((hit, i) => (
          <div
            key={`${hit.kind}-${hit.id}`}
            onClick={() => go(hit)}
            onMouseEnter={() => setActive(i)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 16px",
              cursor: "pointer",
              background: i === active ? "var(--row-highlight)" : undefined,
            }}
          >
            <span style={{ fontWeight: 500 }}>{hit.label}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {hit.hint && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{hit.hint}</Typography.Text>
              )}
              <Tag bordered={false}>{KIND_LABEL[hit.kind]}</Tag>
            </span>
          </div>
        ))}
        {rows.length === 0 && (
          <div style={{ padding: "28px 16px" }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                normalize(q).length < 2
                  ? "Tapez au moins deux caractères"
                  : loading
                  ? "Recherche…"
                  : "Aucun résultat"
              }
            />
          </div>
        )}
      </div>

      <div style={{ padding: "8px 16px", borderTop: "1px solid var(--app-border)" }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          ↑ ↓ pour naviguer · Entrée pour ouvrir · Échap pour fermer
        </Typography.Text>
      </div>
    </Modal>
  );
};

export default GlobalSearch;
